import React, { useRef } from 'react';

import { useIntl } from 'react-intl';

import { Form, Segmented } from 'antd';
import type { FormInstance } from 'antd/es/form';

// The eventTypes constants import this component, so take the type names from commons.
import { TYPE_OF_COMPONENTS as COMPONENT_TYPES } from '@src/constants/commons';
import { useSelectFieldContext } from '@src/packages/eventTypes/context/SelectFieldContext';

type SelectType = typeof COMPONENT_TYPES.SELECT | typeof COMPONENT_TYPES.LIST_SELECT;

interface SourceSwitchProps {
  value?: SelectType;
  onChange?: (value: SelectType) => void;
  /** Not `id`: Form.Item injects its own `id` (the input's DOM id) into its child. */
  fieldIndex: number;
  form: FormInstance;
}

/**
 * "Options" stores them in the form (`select`); "Saved list" points at an option list
 * (`listSelect`). Switching keeps what each side had, so going back and forth loses nothing until
 * the form is saved.
 */
const SourceSwitch: React.FC<SourceSwitchProps> = ({ value, onChange, fieldIndex, form }) => {
  const intl = useIntl();
  const { onDirty } = useSelectFieldContext();
  const stashed = useRef<Partial<Record<SelectType, Record<string, unknown>>>>({});
  const propsPath = ['components', fieldIndex, 'componentProps'];

  const switchTo = (next: string | number) => {
    const nextType = next as SelectType;
    if (!value || nextType === value) return;
    stashed.current[value] = form.getFieldValue(propsPath);
    onChange?.(nextType);
    form.setFieldValue(
      propsPath,
      stashed.current[nextType] ?? (nextType === COMPONENT_TYPES.SELECT ? { options: [] } : {}),
    );
    onDirty?.();
  };

  return (
    <Segmented
      block
      onChange={switchTo}
      options={[
        {
          label: intl.formatMessage({ id: 'eventTypes.component.componentProps.source.options' }),
          value: COMPONENT_TYPES.SELECT,
        },
        {
          label: intl.formatMessage({ id: 'eventTypes.component.componentProps.source.list' }),
          value: COMPONENT_TYPES.LIST_SELECT,
        },
      ]}
      value={value}
    />
  );
};

interface SelectSourceProps {
  id: number;
  fieldKey: number;
  form: FormInstance;
}

/** Where a selector's options come from; bound to the field's `type`. */
const SelectSource: React.FC<SelectSourceProps> = ({ id, fieldKey, form }) => {
  const intl = useIntl();
  return (
    <Form.Item
      fieldKey={[fieldKey, 'type']}
      label={intl.formatMessage({ id: 'eventTypes.component.componentProps.source' })}
      name={[id, 'type']}
    >
      <SourceSwitch fieldIndex={id} form={form} />
    </Form.Item>
  );
};

export default SelectSource;
